<script lang="ts">
    import Icon from '../../components/Icon.svelte';
    import { useNavigate } from 'svelte-navigator';
    import authStore from '../../store/auth';
    import ButtonRounded from '../../common/ButtonRounded.svelte';
    import { sendFeedback } from '../../domain/session/receivers.actions';
    import type { Rate } from '../../types/user';

    const navigate = useNavigate();
    
    let contact: Rate = {
        category: null,
        name: null,
        email: null,
        message: null,
        rating: null,
        mobile: null
    }
    
    let event = ""
    let errors: Record<string, string> = {}
    let stages: string[] = [
        "start",
        "review",
        "thank-you"
    ]

    let stage: number = $authStore.user.email ? 1 : 0;

    let rates: string[] = ['😍', '🙂', '😐', '🙁', '😖'];
    let selectedRate: number = null;

    const selectRate = idx => {
        selectedRate = idx
    }

    const isValidEmail = email => {
        const emailRegex = new RegExp(/^[A-Za-z0-9_!#$%&'*+\/=?`{|}~^.-]+@[A-Za-z0-9.-]+$/, "gm");
        return emailRegex.test(email);
    }

    const processEmail = () => {
        if (!contact.email || (contact.email && !isValidEmail(contact.email))) {
            errors.email = "Email is required";
            return
        }

        authStore.setEmail(contact.email);

        stage = 1;
    }

    const processRatings = async () => {
        const emailToCheck = contact.email || $authStore.user?.email;
        if (!emailToCheck || (emailToCheck && !isValidEmail(emailToCheck))) {
            errors.email = "Email is required";
            return
        }
        
        const user = $authStore.user
        const data = {
            ...contact,
            ...user
        }

        authStore.setUser(data);
        event = "loading";

        try {
            const payload: Rate = {
                ...contact,
                name: `${user.first_name} ${user.last_name}`,
                email: emailToCheck,
                mobile: user.mobile,
                rating: selectedRate + 1
            }
            await sendFeedback(payload)
            stage = 2;
        } catch (e) {} finally {
            event = "";
        }
    }
    
</script>

<div class="flex h-full justify-center items-center">
    <div class="w-full">
        <div class="display-flex">
            <div class="flex flex-col">
                {#if stages[stage] === stages[0]}
                <div class="relative mx-8 my-16 secondary-text-color">
                    <strong class="primary-text-color text-lg">
                        Thank you for contacting us! 
                    </strong>
                    Please provide your email address below to receive a summary of the call.
                    <div class="mt-4">
                        <span class="text-xs">
                            Enter your email address
                        </span>
                        <div class="flex">
                            <input
                                placeholder="Enter your email"
                                bind:value={contact.email}
                                required
                                class="border-0 border-b border-black mr-2 px-2 py-1 text-sm primary-text-color"
                            />
                            <ButtonRounded
                                disabled={false}
                                on:click={processEmail}
                                size=8
                                title=""
                                icon="right-arrow"
                                iconColor="#30A059"
                                iconSize=16
                                className="call-btn-bg-color w-10 h-8"
                            />
                        </div>
                    </div>
                    {#if errors.email}
                        <small class="error">{ errors.email }</small>
                    {/if}
                </div>
                {/if}
                {#if stages[stage] === stages[1]}
                <div class="relative mx-8 mt-16 mb-8 secondary-text-color">
                    <strong class="primary-text-color text-xl">
                        We value your feedback
                    </strong>
                    <small class="secondary-text-color">
                        Please rate your call experience below.
                    </small>
                    <div class="mt-4">
                        <span class="text-xs">
                            How do you feel about this call?
                        </span>
                        <div>
                            {#each rates as rate, i}
                                <!-- svelte-ignore a11y-click-events-have-key-events -->
                                <span 
                                    class="mr-2 border-4 border-gray-100 rounded-full bg-gray-100 w-8 h-8 inline-flex justify-center items-center"
                                    class:rate={selectedRate !== i}
                                    on:click={() => selectRate(i)}
                                >{rate}</span>
                            {/each}
                        </div>
                    </div>
                    <div class="mt-4">
                        <span class="text-xs">
                            Write your review
                        </span>
                        <div class="flex">
                            <input
                                placeholder="Enter review"
                                bind:value={contact.message}
                                class="border-0 border-b border-black mr-2 px-2 py-1 text-sm primary-text-color"
                            />
                            <ButtonRounded
                                disabled={false}
                                on:click={processRatings}
                                size=8
                                title=""
                                icon="right-arrow"
                                iconColor="#30A059"
                                iconSize=16
                                className="call-btn-bg-color w-10 h-8"
                            />
                        </div>
                        {#if errors.message}
                            <small class="error">{ errors.message }</small>
                        {/if}
                    </div>
                </div>
                {/if}
                {#if stages[stage] === stages[2]}
                <div class="relative flex flex-col justify-center items-center mx-8 my-16 secondary-text-color text-center">
                    <img src="images/icons/star.png" width="50px" alt="star">
                    <strong class="primary-text-color">
                        Thank you for providing
                        your feedback!
                    </strong>
                    <br/>
                    <span class="text-xs">
                        We appreciate your input and strive to continuously improve our 
                        services based on your experience. We have also sent a summary of this call 
                        to your email.
                    </span>
                    <ButtonRounded
                        disabled={false}
                        on:click={() => navigate("/")}
                        size=8
                        title=""
                        className="call-btn-bg-color h-10 mt-4 flex-inline text-white px-8"
                    >
                    Return home
                    </ButtonRounded>

                </div>
                {/if}
                {#if event !== ""}
                <div class="flex mx-1">
                    <span class={`${event === "loading" ? "animate-spin" : ""} mx-2`}>
                        <Icon icon={event} size={24} color="#30A059" />
                    </span>
                    <span class="mx-2">
                        {#if event === "loading"}
                            Connecting.
                        {:else}
                            Ringing.
                        {/if}
                    </span>
                </div>
                {/if}
            </div>
        </div>

    </div>
</div>

<style lang="scss">
    .primary-text-color {
        color: var(--primary-text-color)
    }
    .secondary-text-color {
        color: var(--secondary-text-color)
    }
    .primary-bg-text-color {
        color: var(--primary-bg-color)
    }
    .secondary-bg-text-color {
        color: var(--secondary-bg-color)
    }
    .rate {
        filter: grayscale(1);
    }
</style>